import content from "./grammar-lab-content.json";
import { addDays } from "./utils";

// Only these statuses are safe to show learners — pending_consensus content
// must never render. This is a real runtime check against the field, not an
// assumption that "all points currently qualify." source_confirmed (added
// v1.3) is a stronger status than consensus_confirmed — it means the point
// was additionally cross-checked against a named authoritative source
// (see each point's own `source`/`corroborating_sources` fields).
const VISIBLE_STATUSES = new Set(["consensus_confirmed", "human_confirmed", "source_confirmed"]);

// Per-target-language content datasets. Only Turkish exists today, so every accessor below
// resolves to it regardless of the langCode it's given — but the lookup is centralized here so
// that adding a second language later is a one-line addition to this map, not a change to any
// of this module's consumers (GrammarLabView.js, babiCheckins.js, etc. all call the accessors
// below with the active targetLanguage already).
const CONTENT_BY_LANG = {
  turkish: content,
};

function resolveContent(langCode) {
  return CONTENT_BY_LANG[langCode?.toLowerCase()] || content;
}

// Memoized per dataset (not per langCode — several codes can share one dataset) so repeated
// calls don't re-filter/re-derive the same underlying JSON on every render.
const pointsCache = new Map();
function computeVisiblePoints(rawContent) {
  return (rawContent.grammar_points || []).filter((gp) => VISIBLE_STATUSES.has(gp.verification_status));
}

export function getGrammarPoints(langCode) {
  const raw = resolveContent(langCode);
  if (!pointsCache.has(raw)) pointsCache.set(raw, computeVisiblePoints(raw));
  return pointsCache.get(raw);
}

export function getGrammarCategories(langCode) {
  return [...new Set(getGrammarPoints(langCode).map((g) => g.category))];
}

export function getGrammarCefrLevels(langCode) {
  return [...new Set(getGrammarPoints(langCode).map((g) => g.cefr_level))].sort();
}

export function getGrammarPoint(langCode, grammarPointId) {
  return getGrammarPoints(langCode).find((g) => g.grammar_point_id === grammarPointId) || null;
}

// Fingerprints exactly the English source fields translateGrammarPoint (api.js) actually reads
// (title/explanation/rule_summary plus each form-or-item's meaning and example translations) —
// deliberately narrower than "the whole point object" so unrelated metadata edits (verification_
// status, source citations, tags) don't force every cached translation to regenerate. Stored
// alongside a cached translation and re-checked on every read (see getGrammarLabTranslationCache
// call sites): a mismatch means the English source changed since this translation was generated,
// so the cache entry is stale and must be treated as a miss. This is what the GP0068-in-Farsi bug
// was missing — the cache had no way to know its source content had since been fixed. FNV-1a,
// not cryptographic — this only needs to change when the input changes, not resist tampering.
export function computeContentFingerprint(gp) {
  const isUsageList = gp.point_type === "usage_list";
  const isStructural = gp.point_type === "structural";
  const entries = isStructural ? (gp.examples || []) : isUsageList ? (gp.items || []) : (gp.forms || []);
  const idField = isStructural ? "example_id" : isUsageList ? "item_id" : "form_id";
  const entryPart = isStructural
    ? entries.map((e) => `${e[idField]}:${e.translation}:${e.structural_note || ""}`).join(";")
    : entries
        .map((e) => `${e[idField]}:${e.meaning}:${e.base_meaning || ""}:${(e.examples || []).map((ex) => ex.translation || "").join("|")}`)
        .join(";");
  const raw = `${gp.title}|${gp.explanation}|${gp.rule_summary}|${entryPart}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < raw.length; i++) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

// Groups a point's forms by their example base word (e.g. all 6 persons of
// "gelmek" read as one paradigm) instead of a flat list — derived entirely
// from the existing `base` field, no new taxonomy invented.
export function groupForms(forms) {
  const order = [];
  const byBase = new Map();
  for (const f of forms || []) {
    if (!byBase.has(f.base)) { byBase.set(f.base, []); order.push(f.base); }
    byBase.get(f.base).push(f);
  }
  return order.map((base) => ({
    base,
    baseMeaning: byBase.get(base)[0].base_meaning,
    forms: byBase.get(base),
  }));
}

// ─── Independent review cadence (NOT FSRS, NOT the flashcard overwhelm gate) ───
// Fixed ladder: 1d -> 3d -> 7d -> 14d -> 30d. Advance a rung on a correct
// session, reset to the first rung on an incorrect one.
export const CADENCE_DAYS = [1, 3, 7, 14, 30];

export function isGrammarPointDue(entry, todayISOStr) {
  if (!entry || !entry.dueDate) return true; // never reviewed = due now
  return entry.dueDate <= todayISOStr;
}

// Distinct from isGrammarPointDue (which drives the spaced-repetition due
// count): this just answers "has the learner ever opened this point's
// detail view," for the simple per-card Reviewed / Not Reviewed badge.
export function isGrammarPointViewed(entry) {
  return !!entry?.viewed;
}

export function reviewGrammarPoint(entry, correct, todayISOStr) {
  const prevStep = entry && typeof entry.cadenceStep === "number" ? entry.cadenceStep : -1;
  const step = correct ? Math.min(prevStep + 1, CADENCE_DAYS.length - 1) : 0;
  return {
    cadenceStep: step,
    dueDate: addDays(todayISOStr, CADENCE_DAYS[step]),
    lastReviewed: todayISOStr,
    timesCorrect: (entry?.timesCorrect || 0) + (correct ? 1 : 0),
    timesIncorrect: (entry?.timesIncorrect || 0) + (correct ? 0 : 1),
  };
}

export function countGrammarDue(langCode, grammarProgress, todayISOStr) {
  return getGrammarPoints(langCode).filter((gp) => isGrammarPointDue(grammarProgress?.[gp.grammar_point_id], todayISOStr)).length;
}

// Distinct from countGrammarDue (spaced-repetition due count, which stays high
// because unreviewed points are always "due"): this is the simple total-minus-
// viewed count shown on the bottom nav badge, matching the per-card Reviewed /
// Not Reviewed badge (isGrammarPointViewed) rather than the review cadence.
export function countGrammarNotReviewed(langCode, grammarProgress) {
  return getGrammarPoints(langCode).filter((gp) => !isGrammarPointViewed(grammarProgress?.[gp.grammar_point_id])).length;
}

// ═══════════════════════════════════════════════════════════════
// PRACTICE — multiple-choice drills derived programmatically from
// `forms` (prompt = English meaning, options = surface forms from the
// same point). Graded by exact string match; no free-text AI grading.
// Shared by GrammarLabView's own practice sessions and the placement
// test (placementTest.js), which reuses it as-is for grammar questions.
// ═══════════════════════════════════════════════════════════════
export function buildQuestions(gp, count = 5, langCode = "turkish") {
  // usage_list points have no forms[] — build the same {form_id, surface,
  // meaning} shape from items[] (word -> surface) so the rest of the
  // question-building logic below is identical for both point types.
  const pool = gp.point_type === "usage_list"
    ? (gp.items || []).filter((it) => it.word && it.meaning).map((it) => ({ form_id: it.item_id, surface: it.word, meaning: it.meaning, tags: {} }))
    : (gp.forms || []).filter((f) => f.surface && f.meaning);
  const surfaces = [...new Set(pool.map((f) => f.surface))];
  // contrast_example forms (e.g. GP0016's "top/topu" — included to show a
  // word that does NOT soften) carry editorial commentary in their own
  // `meaning` field (" — does NOT soften"). That's fine as detail-view prose
  // but reads as a broken/confusing quiz prompt, so they're still valid
  // distractors (via `surfaces` above) but never asked about directly.
  const promptable = pool.filter((f) => !f.tags?.contrast_example);
  const source = promptable.length ? promptable : pool;
  const n = Math.min(count, source.length);
  const picked = [...source].sort(() => Math.random() - 0.5).slice(0, n);
  return picked.map((correct) => {
    // Prefer distractors that share the correct answer's base word (e.g. other persons of the
    // same verb: yazmıyorum/yazmıyorsun/yazmıyoruz alongside yazmıyorlar). Several paradigm
    // points illustrate one pattern across more than one base verb (e.g. gelmek AND yazmak, to
    // show front/back vowel harmony) — picking distractors from the whole point indiscriminately
    // let a question like "they are not writing" end up with options from "gelmek" (to come) as
    // the ONLY wrong answers, so the root word alone gave the answer away without the learner
    // needing to know the grammar point (person suffix) at all. Same-base forms differ only in
    // the part the point is actually teaching, so they're tried first; only once those run out do
    // we widen to the rest of the point's surfaces, then (via padDistractors) to other points.
    const sameBaseSurfaces = [...new Set(
      pool.filter((f) => f.base !== undefined && f.base === correct.base && f.surface !== correct.surface).map((f) => f.surface)
    )].sort(() => Math.random() - 0.5);
    let distractors = sameBaseSurfaces.slice(0, 3);
    // A point's own forms/items don't always provide 3 distinct same-base distractors — several
    // usage_list points have as few as 1 item total (e.g. GP0066, GP0057), and some paradigm
    // points have only 2 persons/forms for their base verb. Widen to the rest of the point's
    // surfaces, then (via padDistractors) to other points, rather than shipping a too-easy/
    // degenerate question — but only to fill what same-base distractors couldn't provide, so a
    // question never trades an available same-base distractor for a cross-base one.
    if (distractors.length < 3) {
      const restOfPointSurfaces = surfaces.filter((s) => s !== correct.surface && !distractors.includes(s));
      let fillerPool = restOfPointSurfaces;
      if (distractors.length + fillerPool.length < 3) fillerPool = padDistractors(fillerPool, correct.surface, gp, langCode);
      const filler = fillerPool.sort(() => Math.random() - 0.5).slice(0, 3 - distractors.length);
      distractors = [...distractors, ...filler];
    }
    const options = [...distractors, correct.surface].sort(() => Math.random() - 0.5);
    return { formId: correct.form_id, prompt: correct.meaning, answer: correct.surface, options };
  });
}

// Surface forms of one grammar point, regardless of point_type — same {surface|word} duality
// buildQuestions itself normalizes above.
function surfacesOfPoint(otherGp) {
  const raw = otherGp.point_type === "usage_list" ? (otherGp.items || []) : (otherGp.forms || []);
  return raw.map((it) => it.surface || it.word).filter(Boolean);
}

// Fills distractorPool up to 3 using other grammar points' surface forms — same CEFR level
// first (keeps distractors plausible for the tier being tested), then any level if that tier
// alone still isn't enough. Never includes the correct answer or a duplicate.
function padDistractors(distractorPool, correctSurface, gp, langCode) {
  const seen = new Set([...distractorPool, correctSurface]);
  const extra = [];
  const otherPoints = getGrammarPoints(langCode).filter((p) => p.grammar_point_id !== gp.grammar_point_id);
  const sameTier = otherPoints.filter((p) => p.cefr_level === gp.cefr_level);
  const otherTiers = otherPoints.filter((p) => p.cefr_level !== gp.cefr_level);
  for (const candidates of [sameTier, otherTiers]) {
    for (const p of candidates) {
      for (const s of surfacesOfPoint(p)) {
        if (seen.has(s)) continue;
        seen.add(s);
        extra.push(s);
      }
      if (distractorPool.length + extra.length >= 3) break;
    }
    if (distractorPool.length + extra.length >= 3) break;
  }
  return [...distractorPool, ...extra];
}
